import { useEffect, useState } from "react";
import { usePage } from "@inertiajs/react";
import { AppModal } from "@/components/ui/app-modal";
import { Button } from "@/components/ui/button";
import { MessageCircle, Bell, CalendarClock, Zap } from "lucide-react";

type LineFriendPrompt = {
  shouldShow: boolean;
  friendAddUrl: string | null;
  botDisplayName: string | null;
};

type PageProps = {
  line_friend_prompt?: LineFriendPrompt | null;
  [key: string]: unknown;
};

export function LineFriendPromptModal() {
  const { props, url } = usePage<PageProps>();
  const prompt = props.line_friend_prompt ?? null;

  // 次のページ遷移でまた表示する（E案）ため url をキーに open 状態を初期化
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(Boolean(prompt?.shouldShow));
  }, [prompt?.shouldShow, url]);

  if (!prompt?.shouldShow) return null;

  const handleFriendAdd = () => {
    if (!prompt.friendAddUrl) return;
    window.open(prompt.friendAddUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title="LINEで、もっと便利に。"
      description="Ukarte のLINE公式アカウントを友だち追加して、LINE上で各種機能を使いましょう。"
      size="md"
      closeOnOutsideClick
      footer={
        <div className="flex flex-col gap-2 w-full sm:flex-row sm:justify-end">
          <Button
            onClick={handleFriendAdd}
            disabled={!prompt.friendAddUrl}
            className="bg-[#06C755] hover:bg-[#05a647] text-white sm:order-2"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            友だち追加する
          </Button>
        </div>
      }
    >
      <ul className="space-y-4 py-2">
        <li className="flex gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#06C755]/10 text-[#06C755]">
            <Zap className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">LINEで話しかけるだけで機能が使える</p>
            <p className="text-sm text-muted-foreground">
              「スケジュール登録」「面談申し込み」「面接練習作成」など、アプリを開かずにLINEのトークから直接操作できます。
            </p>
          </div>
        </li>
        <li className="flex gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#06C755]/10 text-[#06C755]">
            <Bell className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">面談の確定通知がLINEで届く</p>
            <p className="text-sm text-muted-foreground">
              キャリアアドバイザーとの面談日時が確定した瞬間、LINEにお知らせが届きます。
            </p>
          </div>
        </li>
        <li className="flex gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#06C755]/10 text-[#06C755]">
            <CalendarClock className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">今日/今後の予定をLINEから即確認</p>
            <p className="text-sm text-muted-foreground">
              「今日の予定」「今後のタスク」と送るだけで、選考・面談・ToDoをまとめて確認できます。
            </p>
          </div>
        </li>
      </ul>
    </AppModal>
  );
}
